'use client';

import React, { useMemo, useState } from 'react';
import { ReactComponent as Tag } from '@assets/icons/tag.svg';
import { ReactComponent as Trash } from '@assets/icons/trash-can.svg';
import { defaultBasketData, ROUTES } from 'constant';
import Image from 'next/image';
import Link from 'next/link';
import {
  useDeleteShoppingCartItemMutation,
  useGetShoppingCartQuery,
  useRemoveGiftCardPaymentMutation,
  useUpdateShoppingCartItemMutation
} from 'services/shoppingCartApi';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
  Tooltip,
  useBoolean,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch } from 'store/hooks';
import {
  removeFromLocalCart,
  setFulfillmentPreference,
  updateLocalCartQuantity
} from 'store/reducers/cartSlice';
import { setParentPageType } from 'store/reducers/productsSlice';

import { formatCurrency } from 'utils';

import {
  CartItemOnlineFulfillmentEnum,
  ProductRedirectionType,
  ShopCartItem,
  ShopCartType,
  UserFulfillmentMode
} from 'types';

import { FulfillmentSelector } from 'components/FulfillmentSelector';
import { Button, Counter, Divider, Loader } from 'ui';

interface ShopItemProps {
  item: ShopCartItem;
  invoiceId: number;
  discount: number;
  isAuthenticated?: boolean;
  isShowTag: boolean;
}

const ShopItem = ({
  item,
  invoiceId,
  discount = 0,
  isAuthenticated = false,
  isShowTag
}: ShopItemProps) => {
  const {
    colors: {
      palette: {
        shop: {
          cart: { detailsStep: styles }
        }
      }
    }
  } = useTheme();

  const dispatch = useAppDispatch();
  const [count, setCount] = useState<number>(item.qty);
  const [loading, setLoading] = useBoolean(true);

  const { data = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isAuthenticated
  });

  const [deleteShoppingCartItem, { isLoading: isDeleting }] =
    useDeleteShoppingCartItemMutation();
  const [updateShoppingCartItem, { isLoading: isUpdating }] =
    useUpdateShoppingCartItemMutation();
  const [removeGiftCardPayment] = useRemoveGiftCardPaymentMutation();

  const isMixedDeliveryAvailable =
    item?.online_fulfillment ===
    CartItemOnlineFulfillmentEnum.ShippingAndPickup;

  const handleChangeMode = (mode: UserFulfillmentMode) => {
    if (item?.online_fulfillment !== undefined) {
      dispatch(
        setFulfillmentPreference({
          cart_item_id: item.cart_item_id,
          original_fulfillment: item.online_fulfillment,
          user_selected: mode,
          invoice_id: invoiceId
        })
      );
    }
  };

  const removeGiftCardPaymentHandler = () => {
    if (isAuthenticated && data.payments.length > 0) {
      const pymtIds = data.payments.map((payment) => payment?.in_pymt_id);
      const deletePromises = pymtIds.map((id) =>
        removeGiftCardPayment({
          in_pymt_id: id,
          invoice_id: data.details.invoice_id ?? 0
        })
      );

      return Promise.allSettled(deletePromises);
    }
  };

  const addCount = (quantity: number) => {
    if (isAuthenticated) {
      return updateShoppingCartItem({
        invoiceId,
        cartItemId: item?.cart_item_id ?? 0,
        itemId: item?.id ?? 0,
        itemType: item?.item_type ?? ShopCartType.Product,
        qty: quantity
      })
        .then(() => setCount(quantity))
        .then(removeGiftCardPaymentHandler);
    }
    setCount(quantity);
    const itemType = item?.item_type ?? ShopCartType.Product;
    dispatch(
      updateLocalCartQuantity({
        type: itemType,
        id: item.cart_item_id,
        qty: quantity
      })
    );
  };

  const onDelete = () => {
    if (isAuthenticated) {
      return deleteShoppingCartItem({
        invoiceId,
        cartItemId: item?.cart_item_id ?? 0
      }).then(removeGiftCardPaymentHandler);
    }
    const itemType = item?.item_type ?? ShopCartType.Product;
    dispatch(
      removeFromLocalCart({
        type: itemType,
        id: item.cart_item_id
      })
    );
  };

  const isLoading = isDeleting || isUpdating;

  const path = useMemo(
    () => (item.item_type === ShopCartType.Product ? ROUTES.productsShop : ''),
    [item]
  );

  const oldItemPrice = item.base_price === 0 ? item.amount : item.base_price;
  const newItemPrice = item.amount;
  const priceWithDiscount = item.amount_total + discount;
  const totalWithAllDiscount = newItemPrice * item.qty;

  const renderItemName = () => (
    <Button
      variant='text'
      p={0}
      display='block'
      _hover={{ opacity: path ? 0.5 : 1 }}
      as={Link}
      href={`${path}/${item.id}`}
      onClick={(e) => {
        dispatch(setParentPageType(ProductRedirectionType.ANY_PAGE));
        if (!path) {
          e.preventDefault();
        }
      }}
      whiteSpace='initial'
      height='auto'
      isDisabled={!path}
      _disabled={{
        color: 'initial'
      }}
      cursor={path ? 'pointer' : 'default'}
    >
      <Text
        fontSize={['23px', '26px', '25px']}
        fontWeight='regular'
        color={styles.textColor}
        textAlign={{ base: 'left', lg: 'left' }}
      >
        {item.name}
      </Text>
    </Button>
  );

  const renderImage = () =>
    item?.img && !item?.img.includes('tag.png') ? (
      <Flex
        alignItems='center'
        justifyContent='center'
        bg='white'
        borderRadius='6px'
        overflow='hidden'
        height={120}
        width={120}
        position='relative'
      >
        {loading && <Loader absolute />}
        <Image
          src={item?.img}
          quality={100}
          sizes='(max-width: 450px) 200px, 300px'
          height={120}
          width={120}
          style={{
            objectFit: 'contain',
            aspectRatio: '1/1'
          }}
          alt={`Cart Item Image ${item?.name}`}
          onLoad={setLoading.off}
        />
      </Flex>
    ) : (
      <Flex
        alignItems='center'
        justifyContent='center'
        my='auto'
        position='relative'
        maxH='120px'
        maxW='120px'
        h='120px'
        w='120px'
        bg={styles.emptyImageBg}
        borderRadius='6px'
        overflow='hidden'
        sx={{
          '& > svg': {
            width: { base: '70px', sm: '90px' },
            height: { base: '70px', sm: '90px' }
          }
        }}
      >
        <Tag fill={styles.defaultIconColor} />
      </Flex>
    );

  const renderAmount = () => (
    <Stack
      gap={0}
      flexDirection={{ base: 'row', lg: 'column' }}
      h='100%'
      w='100%'
      justifyContent={{ base: 'space-between', lg: 'center' }}
      alignItems={{ base: 'center', lg: 'flex-end' }}
    >
      <Text
        display={{ base: 'block', lg: 'none' }}
        fontSize='12px'
        color={styles.amountLabel}
        textTransform='uppercase'
      >
        Amount
      </Text>

      <Text
        fontSize={['20px', '23px', '25px']}
        color={styles.amount}
        textAlign='end'
      >
        {`${formatCurrency(newItemPrice)} ea`}
      </Text>
    </Stack>
  );

  return (
    <Box>
      <Divider bg={styles.divider} height='1px' border='none' />
      <VStack
        display={{ base: 'flex', lg: 'none' }}
        pt={{ base: '16px', md: '20px' }}
        pb={{ base: '16px', md: '12px' }}
        px={{ base: 0, md: '12px' }}
        gap='10px'
      >
        <Flex justifyContent='space-between' w='full'>
          {renderItemName()}
          <Button
            w='auto'
            variant='link'
            m={0}
            onClick={onDelete}
            justifyContent='flex-end'
            sx={{
              svg: {
                margin: 0
              }
            }}
          >
            <Trash fill={styles.amount} width={22} height={22} />
          </Button>
        </Flex>
        <Flex w='full' gap='16px'>
          {renderImage()}
          <VStack
            w='full'
            flex={1}
            gap='15px'
            alignItems='flex-start'
            justifyContent='flex-start'
            height='100%'
          >
            <Flex alignItems='flex-end' pt='10px' gap={2} w='full'>
              <Text
                fontSize={['18px', '18px', '25px']}
                color={styles.amount}
                textAlign='left'
              >
                {`${formatCurrency(newItemPrice)} ea`}
              </Text>
              {oldItemPrice !== newItemPrice && (
                <Text
                  textDecoration='line-through'
                  textDecorationThickness='1px'
                  fontSize='14px'
                  color={styles.discountPriceColor}
                  textAlign='end'
                >
                  {formatCurrency(oldItemPrice)}
                </Text>
              )}
            </Flex>

            {isShowTag && (
              <Flex mt='8px'>
                <FulfillmentSelector
                  cartItemId={item.cart_item_id}
                  defaultMode={
                    item.online_fulfillment ||
                    CartItemOnlineFulfillmentEnum.ShippingAndPickup
                  }
                  isDisplayMenu={isMixedDeliveryAvailable}
                  onModeChange={handleChangeMode}
                />
              </Flex>
            )}

            <Flex
              gap='15px'
              flexDirection={{ base: 'column', md: 'row' }}
              w='full'
              justifyContent='space-between'
              alignItems={{ base: 'flex-start', md: 'center' }}
            >
              <Counter
                minValue={1}
                maxValue={99}
                value={count}
                onChange={addCount}
                buttonProps={{
                  bg: styles.itemBg,
                  border: `1px solid ${styles.counterBorder}`,
                  w: { base: '40px', md: '50px' },
                  h: { base: '40px', md: '50px' },
                  p: { base: 0, md: '12px 16px' }
                }}
                styles={{
                  svg: {
                    fill: styles.counterBorder
                  }
                }}
                isDisabled={isLoading}
              />
              <Flex
                alignItems='center'
                pt='10px'
                gap={4}
                justifyContent='flex-end'
                w='full'
              >
                {priceWithDiscount !== totalWithAllDiscount && (
                  <Text
                    textDecoration='line-through'
                    textDecorationThickness='1px'
                    fontSize='14px'
                    color={styles.discountPriceColor}
                    textAlign='end'
                  >
                    {formatCurrency(totalWithAllDiscount)}
                  </Text>
                )}
                <Text
                  fontSize={['23px', '23px', '25px']}
                  color={styles.amount}
                  textAlign='left'
                >
                  {formatCurrency(priceWithDiscount)}
                </Text>
              </Flex>
            </Flex>
          </VStack>
        </Flex>
      </VStack>
      <Grid
        templateColumns='repeat(12, 1fr)'
        templateRows={{ base: 'repeat(4, auto)', lg: 'repeat(2, auto)' }}
        pt={{ base: '16px', md: '20px' }}
        pb={{ base: '6px', md: '12px' }}
        px={{ base: 0, md: '12px' }}
        columnGap='10px'
        rowGap='5px'
        display={{ base: 'none', lg: 'grid' }}
      >
        <GridItem
          colSpan={{ base: 4, sm: 3, md: 2, lg: 7 }}
          rowSpan={{ base: 2, lg: 1 }}
          rowStart={{ base: 0, lg: 1 }}
        >
          <Flex alignItems='center' w='full'>
            {renderImage()}
            <Flex
              flex={1}
              ml='20px'
              gap={0}
              h='100%'
              justifyContent='flex-start'
              flexDirection='column'
              display={{ base: 'none', lg: 'flex' }}
            >
              {renderItemName()}

              <Flex
                alignItems='flex-start'
                pt='10px'
                gap={1}
                w='full'
                mt={1}
                flexDirection='column'
                justifyContent='flex-start'
              >
                <Text
                  fontSize={['14px', '18px', '20px']}
                  color={styles.amount}
                  textAlign='left'
                >
                  {`${formatCurrency(newItemPrice)} ea`}
                </Text>
                {oldItemPrice !== newItemPrice && (
                  <Text
                    textDecoration='line-through'
                    textDecorationThickness='1px'
                    fontSize='16px'
                    color={styles.discountPriceColor}
                    textAlign='end'
                  >
                    {formatCurrency(oldItemPrice)}
                  </Text>
                )}
                {isShowTag && (
                  <Flex mt='8px'>
                    <FulfillmentSelector
                      cartItemId={item.cart_item_id}
                      defaultMode={
                        item.online_fulfillment ||
                        CartItemOnlineFulfillmentEnum.ShippingAndPickup
                      }
                      isDisplayMenu={isMixedDeliveryAvailable}
                      onModeChange={handleChangeMode}
                    />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
        </GridItem>

        {/* for small screen */}
        <GridItem
          colSpan={{ base: 8, sm: 9, md: 10 }}
          rowSpan={1}
          rowStart={{ base: 0, lg: 1 }}
          display={{ base: 'grid', lg: 'none' }}
        >
          <Stack gap={0} h='100%' w='100%' justifyContent='center'>
            {renderItemName()}
          </Stack>
        </GridItem>

        <GridItem
          colSpan={{ base: 8, sm: 9, md: 10, lg: 2 }}
          rowSpan={1}
          rowStart={{ base: 0, lg: 'auto' }}
          my={{ base: '12px', lg: '0' }}
        >
          <Flex
            justifyContent={{ base: 'space-between', lg: 'center' }}
            alignItems='center'
            height='100%'
            minH='45px'
          >
            <Counter
              minValue={1}
              maxValue={99}
              value={count}
              onChange={addCount}
              buttonProps={{
                bg: styles.itemBg,
                border: `1px solid ${styles.counterBorder}`,
                w: { base: '40px', md: '50px' },
                h: { base: '40px', md: '50px' },
                p: { base: 0, md: '12px 16px' }
              }}
              styles={{
                svg: {
                  fill: styles.counterBorder
                }
              }}
              isDisabled={isLoading}
            />

            <Box display={{ base: 'block', lg: 'none' }}>
              <Tooltip
                label='delete'
                shouldWrapChildren
                bg='black'
                color='white'
                borderRadius='2px'
              >
                <Button
                  variant='link'
                  m={0}
                  onClick={onDelete}
                  justifyContent='flex-end'
                  sx={{
                    svg: {
                      margin: 0
                    }
                  }}
                >
                  <Trash fill={styles.amount} width={22} height={22} />
                </Button>
              </Tooltip>
            </Box>
          </Flex>
        </GridItem>

        <GridItem
          display={{ base: 'grid', lg: 'none' }}
          colSpan={12}
          rowStart={{ base: 0, lg: 'auto' }}
          overflow='hidden'
        >
          {renderAmount()}
        </GridItem>

        <GridItem
          colSpan={{ base: 12, lg: 2 }}
          rowStart={{ base: 0, lg: 'auto' }}
          overflow='hidden'
        >
          <Stack
            pb={{ base: '20px', lg: '0' }}
            gap={0}
            flexDirection={{ base: 'row', lg: 'column' }}
            h='100%'
            w='100%'
            justifyContent={{ base: 'space-between', lg: 'center' }}
            alignItems={{ base: 'center', lg: 'flex-end' }}
          >
            <Text
              display={{ base: 'block', lg: 'none' }}
              fontSize='12px'
              color={styles.amountLabel}
              textTransform='uppercase'
            >
              Total
            </Text>

            <Text
              fontSize={['20px', '23px', '25px']}
              color={styles.amount}
              textAlign='end'
            >
              {formatCurrency(priceWithDiscount)}
            </Text>
            {totalWithAllDiscount !== priceWithDiscount && (
              <Text
                pt='4px'
                textDecoration='line-through'
                textDecorationThickness='1px'
                fontSize={['20px']}
                color={styles.discountPriceColor}
                textAlign='end'
              >
                {formatCurrency(totalWithAllDiscount)}
              </Text>
            )}
          </Stack>
        </GridItem>

        <GridItem
          colSpan={{ base: 12, lg: 1 }}
          display={{ base: 'none', lg: 'block' }}
          mt={{ base: '15px', lg: '0' }}
          overflow='hidden'
          alignContent='center'
          textAlign='right'
          _hover={{
            opacity: 0.6
          }}
        >
          <Tooltip
            label='delete'
            p={2}
            shouldWrapChildren
            bg='black'
            color='white'
            borderRadius='2px'
          >
            <Button variant='link' w='auto' onClick={onDelete}>
              <Trash fill={styles.amount} width={22} height={22} />
            </Button>
          </Tooltip>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default ShopItem;
